import React, { useState } from "react";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import { PollActions, useAppDispatch } from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { useNavigate } from "react-router-dom";
import DeviceDetector from "device-detector-js";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import CommentForm from "./CommentForm";
import { useTranslation } from "react-i18next";
import {
    getPollIdsFromCookies,
    persistGuestVotes,
    readGuestVotes,
} from "@Utils/guestVotes";
import { useCookies } from "react-cookie";

interface IVoteConfirmationContent {
    choiceId: string;
    choice: string;
    pollId: string;
    allowComments?: boolean;
}

export const VoteConfirmationContent: React.FC<IVoteConfirmationContent> = ({
    choiceId,
    choice,
    pollId,
    allowComments = true,
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const deviceDetector = new DeviceDetector();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionFailed, setSubmissionFailed] = useState(false);
    const [cookies, setCookie] = useCookies(["tb"]);
    const [commentPayload, setCommentPayload] = useState<{
        body: string;
        username?: string;
    }>({ body: "" });

    const confirmVoteChoice = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true); // Set loading state
        setSubmissionFailed(false);

        try {
            // Send vote
            const outcome = await dispatch(
                PollActions.submitVote(pollId, null, choice, {
                    choice_id: choiceId,
                    device: JSON.stringify(
                        deviceDetector.parse(`${navigator.userAgent}`)
                    ),
                })
            );
            if (!outcome) {
                // Show an error and allow retry when the vote submission fails.
                setSubmissionFailed(true);
                return;
            }

            if (outcome === true) {
                const existingVotes = readGuestVotes();
                persistGuestVotes([
                    ...existingVotes.filter((vote) => vote.pollId !== pollId),
                    { pollId, choiceIds: [choiceId] },
                ]);
            }
            // Notify the mounted poll that this guest can now view results.
            const votedPollIds = getPollIdsFromCookies(cookies);
            setCookie(
                "tb",
                Array.from(new Set([...votedPollIds, pollId])).join(";"),
                { path: "/" }
            );

            // Only add a comment for a new vote; repeated scans must not duplicate comments.
            if (outcome === true && allowComments && commentPayload.body) {
                dispatch(PollActions.addCommentInPoll(pollId, commentPayload));
            }

            // Display the success or already-voted dialog.
            dispatch(
                dialogActions.openDialog({
                    id: DialogComponents.VotedChoiceDialog,
                    props: { choice },
                })
            );
        } finally {
            setIsSubmitting(false); // Reset after submission
        }
    };

    const handleCloseTab = () => {
        dispatch(dialogActions.hideDialog());
        navigate("/");
    };

    return (
        <Box
            sx={{
                textAlign: "center",
                minHeight: "90vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <div>
                <Typography variant="h5">
                    {t("dialogs.vote_confirmation.scanned_option")}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "30px",
                        fontWeight: "bold",
                        color: "#333",
                        margin: "30px 0",
                    }}
                >
                    {choice}
                </Typography>
            </div>
            <Box
                sx={{
                    marginBottom: "20px",
                    width: "100%",
                }}
            >
                {allowComments && (
                    <Box
                        sx={{
                            p: "20px",
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                            mb: "20px",
                        }}
                    >
                        <Typography variant="body1">
                            {t("dialogs.vote_confirmation.shared_thoughts")}
                        </Typography>
                        <CommentForm onCommentChange={setCommentPayload} />
                    </Box>
                )}
                <div>
                    {submissionFailed && (
                        <Alert severity="error">
                            {t("dialogs.vote_confirmation.submission_failed")}
                        </Alert>
                    )}
                    <Button
                        variant="contained"
                        size="large"
                        color="primary"
                        aria-label="Submit your vote and any optional comments"
                        fullWidth
                        onClick={confirmVoteChoice}
                        disabled={isSubmitting}
                        sx={{
                            mt: "20px",
                            p: "10px",
                            color: "#fff",
                            fontWeight: "bold",
                        }}
                    >
                        {isSubmitting
                            ? t("dialogs.vote_confirmation.submitting")
                            : t("dialogs.vote_confirmation.submit_vote")}
                    </Button>
                    <br />
                    <Link
                        onClick={handleCloseTab}
                        disabled={isSubmitting}
                        component="button"
                        color="secondary"
                        sx={{
                            mt: "15px",
                            color: "#d32f2f",
                            textDecoration: "underline",
                            fontSize: "14px",
                        }}
                    >
                        {t("dialogs.vote_confirmation.close_and_scan")}
                    </Link>
                </div>
            </Box>
        </Box>
    );
};

const VoteConfirmationActions: React.FC = () => <></>;

export default {
    content: VoteConfirmationContent,
    actions: VoteConfirmationActions,
    title: "Voted Choice",
    disableCancelButton: true,
    isGuestData: false,
};
